import React, { useCallback, useEffect, useState } from "react";
import DeleteConfirmModal from "./DeleteConfirmModal";
import "./CollaboratorsPanel.css";

type AccessLevel = "view" | "full";
type ServiceAccess = { serviceIndex: number; accessLevel: AccessLevel };
type Collaborator = {
  id: string;
  ownerUserId: string;
  collaboratorUserId: string;
  accessLevel: AccessLevel;
  serviceAccess: ServiceAccess[];
};
type PendingInvite = { id: string; email: string; accessLevel: AccessLevel };
type UserInfo = { name: string; email: string; last_login?: string };

type Props = {
  // Owner whose collaborators these are: the logged-in user on their own page,
  // or the viewed user on the admin view.
  ownerUserId: string;
  // Bump to force a re-fetch (e.g. after an invite is sent from the + tab).
  refreshKey?: number;
};

const COLS = "2fr 3fr 1.4fr 2.4fr 2fr 1.6fr";

// Normalises a collaborator's per-service grants, migrating any legacy
// {serviceIndices, accessLevel} shape into serviceAccess for display.
function normalizeServiceAccess(c: any): ServiceAccess[] {
  if (Array.isArray(c?.serviceAccess)) return c.serviceAccess;
  if (Array.isArray(c?.serviceIndices)) {
    const level: AccessLevel = c.accessLevel === "full" ? "full" : "view";
    return c.serviceIndices.map((i: number) => ({ serviceIndex: i, accessLevel: level }));
  }
  return [];
}

export default function CollaboratorsPanel({ ownerUserId, refreshKey }: Props) {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [users, setUsers] = useState<Record<string, UserInfo>>({});
  const [services, setServices] = useState<any[]>([]);
  const [ownerServiceIndices, setOwnerServiceIndices] = useState<number[]>([]);
  // The collaborator whose service access is being edited in the modal, plus a
  // draft map of serviceIndex -> level (present = granted).
  const [editAccess, setEditAccess] = useState<Collaborator | null>(null);
  const [draft, setDraft] = useState<Record<number, AccessLevel>>({});
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; label: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const ownerQuery = ownerUserId ? `?ownerUserId=${encodeURIComponent(ownerUserId)}` : "";

  // Fast path: the collaborator list + services + the owner's assignment all
  // come from Cosmos and resolve quickly. Names/last-login (Auth0-backed
  // /approved_users) load separately below so the table isn't held up by them.
  const load = useCallback(async () => {
    try {
      const [collabRes, svcRes, asgnRes] = await Promise.all([
        fetch(`${baseUrl}/user-collaborators${ownerQuery}`, { credentials: "include" }),
        fetch(`${baseUrl}/smrtdocs/services`, { credentials: "include" }),
        fetch(`${baseUrl}/smrtdocs/assignments`, { credentials: "include" }),
      ]);

      const collabData = await collabRes.json().catch(() => ({}));
      if (!collabRes.ok) throw new Error(collabData?.error || "Failed to load collaborators.");

      const svcData = await svcRes.json().catch(() => ({}));
      const svcArr = svcData.services || [];

      const asgnData = await asgnRes.json().catch(() => ({}));
      const asgn = (asgnData.assignments || {})[ownerUserId];
      let ownerIdx: number[] = [];
      if (asgn?.mode === "single") ownerIdx = [asgn.value];
      else if (asgn?.mode === "multi") ownerIdx = asgn.values || [];

      const collabs: Collaborator[] = (
        Array.isArray(collabData.collaborators) ? collabData.collaborators : []
      ).map((c: any) => ({ ...c, serviceAccess: normalizeServiceAccess(c) }));

      setCollaborators(collabs);
      setInvites(Array.isArray(collabData.invites) ? collabData.invites : []);
      setServices(svcArr);
      setOwnerServiceIndices(ownerIdx);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load collaborators.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, ownerQuery, ownerUserId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Resolve names/last-login in the background (slower Auth0 call) — the list
  // renders first, then names fill in when this resolves.
  useEffect(() => {
    fetch(`${baseUrl}/approved_users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        const arr = d.users || d || [];
        const map: Record<string, UserInfo> = {};
        for (const u of arr) {
          const id = u.user_id || u.id;
          if (id) map[id] = { name: u.name || "", email: u.email || "", last_login: u.last_login };
        }
        setUsers(map);
      })
      .catch(() => {});
  }, [baseUrl]);

  const patch = async (id: string, body: any) => {
    const res = await fetch(`${baseUrl}/user-collaborators/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d?.error || "Update failed.");
    }
  };

  const openAccess = (c: Collaborator) => {
    const d: Record<number, AccessLevel> = {};
    for (const sa of c.serviceAccess || []) d[sa.serviceIndex] = sa.accessLevel;
    setDraft(d);
    setEditAccess(c);
  };
  // Ticking a level grants the service at that level; ticking the level already
  // set un-grants the service; the two levels are mutually exclusive per service.
  const toggleLevel = (idx: number, level: AccessLevel) => {
    setDraft((d) => {
      const next = { ...d };
      if (next[idx] === level) delete next[idx];
      else next[idx] = level;
      return next;
    });
  };
  const saveAccess = async () => {
    if (!editAccess) return;
    const id = editAccess.id;
    const serviceAccess: ServiceAccess[] = Object.entries(draft).map(([i, level]) => ({
      serviceIndex: Number(i),
      accessLevel: level,
    }));
    const prev = collaborators;
    setCollaborators((cs) => cs.map((c) => (c.id === id ? { ...c, serviceAccess } : c)));
    setEditAccess(null);
    try {
      await patch(id, { serviceAccess });
    } catch (e: any) {
      setCollaborators(prev);
      setError(e.message);
    }
  };

  const remove = async (id: string) => {
    try {
      const res = await fetch(`${baseUrl}/user-collaborators/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || "Remove failed.");
      }
      setCollaborators((cs) => cs.filter((c) => c.id !== id));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const svcName = (idx: number) => services[idx]?.name || `Service ${idx + 1}`;

  return (
    <div style={{ marginTop: 10 }}>
      {error && <p style={{ color: "#c62828", fontSize: "0.85rem" }}>{error}</p>}
      <div className="template-card">
        <div className="overview-header" style={{ gridTemplateColumns: COLS }}>
          <div>Name</div>
          <div>Email</div>
          <div>Access Level</div>
          <div>Assigned Services</div>
          <div>Last Login</div>
          <div>Actions</div>
        </div>

        {loading ? (
          <div style={{ color: "#999", padding: "12px" }}>Loading…</div>
        ) : collaborators.length === 0 && invites.length === 0 ? (
          <div style={{ color: "#999", padding: "12px" }}>No collaborators yet.</div>
        ) : (
          <>
            {collaborators.map((c) => {
              const info = users[c.collaboratorUserId];
              const grants = c.serviceAccess || [];
              return (
                <div key={c.id} className="overview-row" style={{ gridTemplateColumns: COLS }}>
                  <div>{info?.name || info?.email || c.collaboratorUserId}</div>
                  <div style={{ textAlign: "left" }}>{info?.email || "—"}</div>
                  <div>
                    {grants.length === 0
                      ? <span style={{ color: "#d4a017" }}>Not granted</span>
                      : grants.map((sa) => (
                          <div key={sa.serviceIndex}>
                            {sa.accessLevel === "full" ? "Full Access" : "View Only"}
                          </div>
                        ))}
                  </div>
                  <div>
                    {grants.length === 0
                      ? <span style={{ color: "#d4a017", fontSize: "0.8rem" }}>No services granted yet — click ☑ to give access</span>
                      : grants.map((sa) => <div key={sa.serviceIndex}>{svcName(sa.serviceIndex)}</div>)}
                  </div>
                  <div>
                    {info?.last_login ? new Date(info.last_login).toLocaleString() : "—"}
                  </div>
                  <div className="smrtdocs-actions">
                    <button
                      className="btn-icon smrtdocs-btn-multi"
                      title="Assign Multiple Services"
                      onClick={() => openAccess(c)}
                    >
                      ☑
                    </button>
                    <button
                      type="button"
                      className="collab-btn-icon collab-btn-delete"
                      style={{ marginLeft: 8 }}
                      title="Remove collaborator"
                      onClick={() =>
                        setConfirmRemove({
                          id: c.id,
                          label: info?.name || info?.email || c.collaboratorUserId,
                        })
                      }
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}

            {invites.map((inv) => (
              <div key={inv.id} className="overview-row" style={{ gridTemplateColumns: COLS }}>
                <div>—</div>
                <div style={{ textAlign: "left" }}>{inv.email}</div>
                <div>—</div>
                <div>—</div>
                <div style={{ color: "#d4a017" }}>Invited</div>
                <div className="smrtdocs-actions" />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Assign Multiple Services — per-service access (View / Full) */}
      {editAccess && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "auto", maxWidth: "90vw" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Assign Services</h2>
            </div>
            <div className="smrtdocs-modal-body">
              {ownerServiceIndices.length === 0 ? (
                <div style={{ color: "#999", fontSize: "0.9rem" }}>
                  No services assigned to this user yet.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="collab-access-grid">
                    <thead>
                      <tr>
                        <th className="collab-access-rowlabel">Services</th>
                        <th>View Only</th>
                        <th>Full Access</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerServiceIndices.map((idx) => (
                        <tr key={idx}>
                          <td className="collab-access-rowlabel">{svcName(idx)}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={draft[idx] === "view"}
                              onChange={() => toggleLevel(idx, "view")}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={draft[idx] === "full"}
                              onChange={() => toggleLevel(idx, "full")}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button className="btn-cancel" onClick={() => setEditAccess(null)}>
                  Cancel
                </button>
                <button className="btn-save" onClick={saveAccess}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => {
          const id = confirmRemove?.id;
          setConfirmRemove(null);
          if (id) remove(id);
        }}
        title="Remove Collaborator"
        message={`Remove ${confirmRemove?.label ?? "this collaborator"} as a collaborator? They will lose access to these documents.`}
        confirmLabel="Remove"
      />
    </div>
  );
}
